// The wire contract with the backend ingest API. See README.md "Wire contract"
// section -- this file is the single source of truth for URL shapes and the
// canonical /chunks payload; keep it in sync with README if either changes.

import { INGEST_ORIGIN, POST_JSON_TIMEOUT_MS } from './config';

function base(recordingId: string): string {
  return `${INGEST_ORIGIN}/api/concept-link-recordings/${encodeURIComponent(recordingId)}`;
}

export const endpoints = {
  chunks: (recordingId: string) => `${base(recordingId)}/chunks`,
  signal: (recordingId: string) => `${base(recordingId)}/signal`,
  poll: (recordingId: string) => `${base(recordingId)}/poll`,
  finalize: (recordingId: string) => `${base(recordingId)}/finalize`,
  abort: (recordingId: string) => `${base(recordingId)}/abort`,
};

/**
 * Canonical /chunks payload: JSON.stringify({seq, events}) serialized ONCE.
 * Both transports (fetch header-auth, and sendBeacon envelope on unload) must
 * carry this EXACT string so the server sees byte-identical bodies either way.
 */
export function canonicalChunkPayload(seq: number, events: unknown[]): string {
  return JSON.stringify({ seq, events });
}

export interface JsonPostResult {
  ok: boolean;
  status: number;
  body: unknown;
}

/** Normal (non-unload) POST: Authorization header + raw JSON string body.
 * Bounded by POST_JSON_TIMEOUT_MS via AbortController so a hung fetch (dead
 * tunnel/proxy) can't block a caller awaiting it forever -- most importantly
 * the finalize flow's drain. An abort surfaces exactly like any other network
 * failure below (ok:false, status:0), so existing retry/preserve-on-failure
 * logic in callers applies unchanged. */
export async function postJson(url: string, token: string, bodyString: string): Promise<JsonPostResult> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), POST_JSON_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: bodyString,
      keepalive: true,
      signal: controller.signal,
    });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // empty/non-JSON body -- fine, callers only need ok/status for most calls.
    }
    return { ok: res.ok, status: res.status, body };
  } catch {
    // network error, CORS failure, timeout/abort, etc.
    return { ok: false, status: 0, body: null };
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Unload drain transport for /chunks. sendBeacon cannot set headers, so the
 * token travels inside the envelope body instead: {session_token, payload}
 * where `payload` is the canonical payload STRING (not a nested object) --
 * the exact same string that would otherwise go in the fetch body.
 * Returns sendBeacon's boolean (false = browser could not queue the send).
 */
export function postBeaconChunk(recordingId: string, token: string, canonicalPayload: string): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') return false;
  const envelope = JSON.stringify({ session_token: token, payload: canonicalPayload });
  const blob = new Blob([envelope], { type: 'text/plain' });
  try {
    return navigator.sendBeacon(endpoints.chunks(recordingId), blob);
  } catch {
    return false;
  }
}
